import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AgGridAtom, ModalAtom } from "../../../components/atoms/atom";
import { FilterBar } from "../../../components/molecule/molecule";
import {
    ACCESS_RIGHT_AREAS
} from "../../../constant/permissions";
import {
    createCompany,
    deleteCompany,
    fetchCompanies,
    updateCompany,
} from "../../../store/slices/companySlice";
import { getSearchAddFilter } from "../../../utils/get-filter-items";
import { showToast } from "../../../utils/method";
import { searchData } from "../../../utils/search";
import AddCompanyModal from "./add-company-modal";
import { getCompanyTableColumns } from "./constant";
import TableSkeleton from "../../../components/atoms/table-skeleton/TableSkeleton";

function CompanySettingPage() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Get company data from Redux store
    const {
        companies,
        loading,
    } = useSelector((state) => state.companies || {
        companies: [],
        loading: { companies: false, create: false, update: false, delete: false },
        error: { companies: null }
    });

    // Get current user for permission checks
    const { user: currentUser } = useSelector((state) => state.auth || {});

    const [filterItems, setFilterItems] = useState(
        getSearchAddFilter({
            addTextKey: "companies.add-company",
        })
    );
    const [filteredCompanyList, setFilteredCompanyList] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);

    // Fetch companies when component mounts
    useEffect(() => {
        dispatch(fetchCompanies())
            .unwrap()
            .catch(error => {
                showToast(error || t("errors.general"), { type: "error" });
            });
    }, [dispatch, t]);

    // Update filtered companies when companies list changes
    useEffect(() => {
        setFilteredCompanyList(companies);
    }, [companies]);

    const onEditCompany = (row) => {
        setSelectedCompany(row);
        setShowEditModal(true);
    };

    const onDeleteCompany = (row) => {
        setSelectedCompany(row);
        setShowDeleteModal(true);
    };

    // Get column definitions
    const companyColumns = getCompanyTableColumns(t, onEditCompany, onDeleteCompany);

    const onFilterAction = (index, key, info) => {
        const localFilterItems = [...filterItems];
        localFilterItems[index].value = info?.value;
        setFilterItems(localFilterItems);

        let filtered = [...companies];

        // Apply all active filters
        localFilterItems.forEach((item) => {
            if (item.key === "search" && item.value) {
                filtered = searchData(
                    item.value,
                    filtered,
                    companyColumns.slice(0, -1),
                );
            }
        });

        setFilteredCompanyList(filtered);

        // Handle add button click
        if (key === "add") {
            setShowAddModal(true);
        }
    };

    const handleAddCompany = async (companyData, allowDuplicateName) => {
        try {
            await dispatch(
                createCompany({
                    ...companyData,
                    ...(allowDuplicateName ? { allowDuplicateName: true } : {}),
                }),
            ).unwrap();
            setShowAddModal(false);
            showToast(t("companies.create-success"), { type: "success" });
            dispatch(fetchCompanies());
        } catch (error) {
            showToast(error || t("errors.general"), { type: "error" });
        }
    };

    // From the add modal's name typeahead: the user picked an existing company,
    // so switch to editing it instead of creating a duplicate.
    const handleSelectExistingCompany = (company) => {
        setShowAddModal(false);
        setSelectedCompany(company);
        setShowEditModal(true);
    };

    const handleEditCompany = async (companyData) => {
        try {
            const { id, ...data } = companyData;
            await dispatch(updateCompany({ id, data })).unwrap();
            setShowEditModal(false);
            setSelectedCompany(null);
            showToast(t("companies.update-success"), { type: "success" });
            dispatch(fetchCompanies());
        } catch (error) {
            showToast(error || t("errors.general"), { type: "error" });
        }
    };

    const handleDeleteCompany = async () => {
        if (!selectedCompany?.id) return;
        try {
            await dispatch(deleteCompany(selectedCompany.id)).unwrap();
            setShowDeleteModal(false);
            setSelectedCompany(null);
            showToast(t("companies.delete-success"), { type: "success" });
            dispatch(fetchCompanies());
        } catch (error) {
            const key =
                typeof error === "object" && error?.message
                    ? error.message
                    : null;
            const interpolation =
                typeof error === "object" && error?.data ? error.data : null;
            if (key) {
                showToast(key, {
                    type: "error",
                    ...(interpolation ? { interpolation } : {}),
                });
            } else {
                showToast(error || t("errors.general"), { type: "error" });
            }
        }
    };

    return (
        <div className="page-container">
            <FilterBar
                items={filterItems}
                onFilterAction={onFilterAction}
                area={ACCESS_RIGHT_AREAS.COMPANIES}
            />

            <div className="flex-grow-1 ag-grid-parent">
                {loading?.companies && filteredCompanyList.length === 0 ? (
                    <TableSkeleton />
                ) : (
                    <AgGridAtom
                        columnDefs={companyColumns}
                        rowData={filteredCompanyList}
                        context={{
                            permissions: currentUser?.permissions || [],
                            currentUserId: currentUser?.id,
                            area: ACCESS_RIGHT_AREAS.COMPANIES,
                        }}
                        enableBrowserTooltips
                        overlayLoadingTemplate=""
                        overlayNoRowsTemplate={`<span class="ag-overlay-no-rows-center">${companies.length === 0 ? t("companies.no-data") : t("common.no-results-found")}</span>`}
                        defaultColDef={{
                            flex: 1,
                            sortable: true,
                            filter: false,
                        }}
                    />
                )}
            </div>

            {showAddModal && (
                <AddCompanyModal
                    show={showAddModal}
                    onHide={() => setShowAddModal(false)}
                    onSubmit={handleAddCompany}
                    loading={loading?.create}
                    existingCompanies={companies}
                    onSelectExisting={handleSelectExistingCompany}
                />
            )}

            {showEditModal && selectedCompany && (
                <AddCompanyModal
                    show={showEditModal}
                    onHide={() => {
                        setShowEditModal(false);
                        setSelectedCompany(null);
                    }}
                    onSubmit={handleEditCompany}
                    loading={loading?.update}
                    initialData={selectedCompany}
                    title={t("companies.edit-company")}
                />
            )}

            {showDeleteModal && selectedCompany && (
                <ModalAtom
                    title={t("companies.delete-company")}
                    body={t("companies.delete-confirm", {
                        name: selectedCompany.name,
                    })}
                    show={showDeleteModal}
                    handleClose={() => {
                        setShowDeleteModal(false);
                        setSelectedCompany(null);
                    }}
                    handleSubmit={handleDeleteCompany}
                    saveText={loading?.delete ? t("common.loading") : t("common.delete")}
                    disabled={loading?.delete}
                    variant="danger"
                />
            )}
        </div>
    );
}

export default CompanySettingPage;
